/**
 * Sniper — reading the market check's `sniper_fire_blocked` reasons (PRED-255,
 * PRED-282).
 *
 *     market_disagrees:ask=<a><min=<m>                  — the external ask of the face bought is under the minimum: not fired
 *     market_disagrees_sibling:<ticker>:ask=<a><min=<m> — another row of the same kick-off release disagreed: not fired
 *     market_disagrees_sibling:<ticker>:no_book         — another row of the release had no book: not fired
 *     market_check_no_book                              — a kick-off buy with no book to read: not fired
 *     market_check_config_error                         — a malformed check: the buy is blocked, fail-closed
 *
 * PRED-288 (wait for the cross, slice below the floor, cheap depth, re-check on the Saque):
 *
 *     market_disagrees:ask=<a><from=<f>                       — period close: under the slice's "from": hard refusal
 *     market_disagrees_waiting:ask=<a><min=<m>                — under the minimum, waiting for the cross (informative)
 *     market_disagrees_partial:ask=<a><min=<m>:pct=<p>:limit=<l> — period close: the slice went out while waiting (informative)
 *     market_disagrees_waiting:no_book                        — no book yet, waiting for one (informative)
 *     market_disagrees_sibling:<ticker>:ask=<a><from=<f>      — read if it comes; a kick-off release carries no slice
 *                                                               (PRED-332), so the engine does not emit it
 *     market_disagrees_sibling:<ticker>:config_error          — another row of the release has a malformed check: not fired
 *     market_disagrees:abandoned:<cause>                      — the wait was called off (informative, except
 *                                                               slice_cancel_unconfirmed: the slice's exposure is unknown)
 *     …:top=<t>:depth=<q>                                     — with cheap depth: the top ask and the depth measured
 *     …:timeout                                               — the wait ran out without the cross: not fired (after
 *                                                               the `<min` and no-book forms, never a `<from` one)
 *     …:recheck_on_kickoff                                    — refused now, re-checked on the Saque (informative)
 *     market_check_crossed:cancel_unconfirmed                 — the cross came, the slice's cancel was not confirmed:
 *                                                               exposure unknown
 *     market_check_abandoned:cancel_unconfirmed               — the wait was called off, the slice's cancel was not
 *                                                               confirmed: exposure unknown
 *
 * The engine appends the suffixes in that order (depth, timeout, recheck) to
 * the row's own refusal, `market_check_no_book` included, and to a sibling one.
 *
 * Every refusal is an alert; the ones that are the MARKET's price disagreeing
 * (`priceDisagrees`) ring their own sound. Waiting, the slice, a called-off
 * wait and a refusal the Saque re-checks are not refusals yet: no alert, no
 * sound — except a slice whose cancel was not confirmed (the wait called off,
 * or the cross reached), whose exposure nobody knows: that one alerts. Every surface (notification chip, operations reason column, sound)
 * asks this file, so the texts and the verdicts cannot drift apart.
 */

const NUM = '(-?\\d*\\.?\\d+)';
const DISAGREES = new RegExp(`^market_disagrees:ask=${NUM}<min=${NUM}$`);
const DISAGREES_FROM = new RegExp(`^market_disagrees:ask=${NUM}<from=${NUM}$`);
const WAITING = new RegExp(`^market_disagrees_waiting:ask=${NUM}<min=${NUM}$`);
const PARTIAL = new RegExp(`^market_disagrees_partial:ask=${NUM}<min=${NUM}:pct=${NUM}:limit=${NUM}$`);
const SIBLING_DISAGREES = new RegExp(`^market_disagrees_sibling:([^:]+):ask=${NUM}<min=${NUM}$`);
const SIBLING_FROM = new RegExp(`^market_disagrees_sibling:([^:]+):ask=${NUM}<from=${NUM}$`);
const SIBLING_NO_BOOK = /^market_disagrees_sibling:([^:]+):no_book$/;
const SIBLING_CONFIG_ERROR = /^market_disagrees_sibling:([^:]+):config_error$/;
const WAITING_NO_BOOK = 'market_disagrees_waiting:no_book';
const ABANDONED = /^market_disagrees:abandoned:([^:]+)(:recheck_on_kickoff)?$/;
const ABANDONED_EXPOSURE_UNKNOWN = 'slice_cancel_unconfirmed';
/** The abandon causes read in words; any other is shown raw. */
const ABANDONED_CAUSE_TEXT: Record<string, string> = {
  release_retracted: 'se retiró la liberación',
  close_retracted: 'se retiró el cierre',
  goal_annulled: 'gol anulado',
};
const SLICE_CANCEL_UNCONFIRMED = /^market_check_(crossed|abandoned):cancel_unconfirmed$/;

const RECHECK_SUFFIX = ':recheck_on_kickoff';
const TIMEOUT_SUFFIX = ':timeout';
const DEPTH_SUFFIX = new RegExp(`:top=${NUM}:depth=${NUM}`);

/** The PRED-288 suffixes peeled off a reason, in whatever order the engine appends them. */
interface ReasonSuffixes {
  base: string;
  recheck: boolean;
  timeout: boolean;
  depth: { top: string; depth: string } | null;
}

function peelSuffixes(reason: string): ReasonSuffixes {
  let base = reason;
  const depthMatch = DEPTH_SUFFIX.exec(base);
  if (depthMatch) base = base.replace(depthMatch[0], '');
  const recheck = base.includes(RECHECK_SUFFIX);
  if (recheck) base = base.replace(RECHECK_SUFFIX, '');
  const timeout = base.endsWith(TIMEOUT_SUFFIX);
  if (timeout) base = base.slice(0, -TIMEOUT_SUFFIX.length);
  return {
    base,
    recheck,
    timeout,
    depth: depthMatch ? { top: depthMatch[1], depth: depthMatch[2] } : null,
  };
}

/** A refusal the Saque re-checks is not final yet: no alert, no sound. */
function withSuffixes(reading: MarketCheckReading, sfx: ReasonSuffixes): MarketCheckReading {
  let text = reading.text;
  if (sfx.depth) text += ` (tope ${sfx.depth.top}, profundidad ${sfx.depth.depth})`;
  if (!sfx.recheck) return { ...reading, text };
  return { text: `${text} — se re-chequea en el saque`, alert: false, priceDisagrees: false };
}

export const MARKET_CHECK_CONFIG_ERROR_REASON = 'market_check_config_error';
export const MARKET_CHECK_NO_BOOK_REASON = 'market_check_no_book';

export interface MarketCheckReading {
  text: string;
  /** Whether the operator has to look: a refused buy, or a broken check. */
  alert: boolean;
  /** The buy was not fired because a market's ask disagreed — this row's or
   *  another row's of the same release. */
  priceDisagrees: boolean;
}

export function marketCheckReading(reason: string | null | undefined): MarketCheckReading | null {
  if (typeof reason !== 'string') return null;
  if (reason === MARKET_CHECK_CONFIG_ERROR_REASON) {
    return { text: 'Chequeo de mercado mal configurado: no compra', alert: true, priceDisagrees: false };
  }
  if (SLICE_CANCEL_UNCONFIRMED.test(reason)) {
    return {
      text: 'Cancelación del tramo sin confirmar: exposición desconocida',
      alert: true,
      priceDisagrees: false,
    };
  }
  const abandoned = ABANDONED.exec(reason);
  if (abandoned) {
    const exposureUnknown = abandoned[1] === ABANDONED_EXPOSURE_UNKNOWN;
    const cause = Object.prototype.hasOwnProperty.call(ABANDONED_CAUSE_TEXT, abandoned[1])
      ? ABANDONED_CAUSE_TEXT[abandoned[1]]
      : abandoned[1];
    if (abandoned[2]) {
      return {
        text: `Espera cancelada (${cause}) — se re-chequea en el saque`,
        alert: false,
        priceDisagrees: false,
      };
    }
    return {
      text: exposureUnknown
        ? `Espera cancelada (${abandoned[1]}): exposición del tramo desconocida`
        : `Espera cancelada (${cause})`,
      alert: exposureUnknown,
      priceDisagrees: false,
    };
  }
  const sfx = peelSuffixes(reason);
  const { base } = sfx;
  const expired = sfx.timeout ? ' — venció la espera' : '';
  if (base === MARKET_CHECK_NO_BOOK_REASON) {
    return withSuffixes(
      { text: `Sin libro para chequear: no compra${expired}`, alert: true, priceDisagrees: false },
      sfx,
    );
  }
  if (!base.startsWith('market_disagrees')) return null;
  const m = DISAGREES.exec(base);
  if (m) {
    const text = sfx.timeout
      ? `No cruzó: ask ${m[1]} < mín ${m[2]} — venció la espera`
      : `Mercado en desacuerdo: ask ${m[1]} < mín ${m[2]}`;
    return withSuffixes({ text, alert: true, priceDisagrees: true }, sfx);
  }
  const f = DISAGREES_FROM.exec(base);
  if (f) {
    return withSuffixes(
      {
        text: `Mercado muy lejos: ask ${f[1]} < desde ${f[2]} — bloqueo duro`,
        alert: true,
        priceDisagrees: true,
      },
      sfx,
    );
  }
  const w = WAITING.exec(base);
  if (w) {
    return withSuffixes(
      { text: `Esperando cruce: ask ${w[1]} < mín ${w[2]}`, alert: false, priceDisagrees: false },
      sfx,
    );
  }
  const p = PARTIAL.exec(base);
  if (p) {
    return withSuffixes(
      {
        text: `Tramo ${p[3]}% a ${p[4]} (ask ${p[1]} < mín ${p[2]})`,
        alert: false,
        priceDisagrees: false,
      },
      sfx,
    );
  }
  const s = SIBLING_DISAGREES.exec(base);
  if (s) {
    return withSuffixes(
      {
        text: `Otra plaza en desacuerdo (${s[1]}): ask ${s[2]} < mín ${s[3]}${expired}`,
        alert: true,
        priceDisagrees: true,
      },
      sfx,
    );
  }
  const sf = SIBLING_FROM.exec(base);
  if (sf) {
    return withSuffixes(
      {
        text: `Otra plaza muy lejos (${sf[1]}): ask ${sf[2]} < desde ${sf[3]} — bloqueo duro${expired}`,
        alert: true,
        priceDisagrees: true,
      },
      sfx,
    );
  }
  const n = SIBLING_NO_BOOK.exec(base);
  if (n) {
    return withSuffixes(
      { text: `Otra plaza sin libro (${n[1]}): no compra${expired}`, alert: true, priceDisagrees: false },
      sfx,
    );
  }
  const c = SIBLING_CONFIG_ERROR.exec(base);
  if (c) {
    return withSuffixes(
      { text: `Otra plaza mal configurada (${c[1]}): no compra`, alert: true, priceDisagrees: false },
      sfx,
    );
  }
  if (base === WAITING_NO_BOOK) {
    return withSuffixes({ text: 'Esperando cruce: sin libro', alert: false, priceDisagrees: false }, sfx);
  }
  // An unrecognised shape of an informative reason never rings the price sound.
  if (base.startsWith('market_disagrees_waiting')) {
    return withSuffixes({ text: 'Esperando cruce', alert: false, priceDisagrees: false }, sfx);
  }
  if (base.startsWith('market_disagrees_partial')) {
    return withSuffixes({ text: 'Tramo bajo el piso', alert: false, priceDisagrees: false }, sfx);
  }
  return withSuffixes({ text: 'Mercado no confirma', alert: true, priceDisagrees: true }, sfx);
}
